if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.toggleButton = this.querySelector('[data-gallery-toggle]');
        this.toggleLabel = this.querySelector('[data-gallery-toggle-label]');
        this.isExpanded = false;

        // Feature flag for collapsible gallery functionality.
        // Set to false to show all product media items.
        this.enableCollapsedGallery = true;

        this.scheduleCollapsibleMediaSetup();

        if (this.toggleButton && !this.toggleButton.dataset.listenerAdded) {
          this.toggleButton.addEventListener('click', () => {
            this.isExpanded = !this.isExpanded;
            this.applyCollapsibleMediaState();
            this.updateCollapsedMediaItems();

            if (!this.isExpanded) {
              this.scrollGalleryIntoView();
            }
          });

          this.toggleButton.dataset.listenerAdded = 'true';
        }

        if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
          subscribe(window.PUB_SUB_EVENTS.variantChange, () => {
            this.scheduleCollapsibleMediaSetup();
          });
        }

        if (!this.elements.thumbnails) return;

        this.getCurrentViewer().addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        this.setupOneSlidePerSwipe();

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setupOneSlidePerSwipe() {
        const viewer = this.getCurrentViewer();
        if (!viewer?.slider) return;

        const slider = viewer.slider;

        this.swipeStartLeft = 0;
        this.swipeStartIndex = 0;
        this.swipeSettleTimer = null;
        this.isTouchingGallery = false;
        this.isCorrectingSwipe = false;

        slider.addEventListener(
          'touchstart',
          () => {
            if (this.mql.matches) return;

            this.isTouchingGallery = true;
            this.swipeStartLeft = slider.scrollLeft;
            this.swipeStartIndex = this.getClosestGallerySlideIndex(viewer, this.swipeStartLeft);

            window.clearTimeout(this.swipeSettleTimer);
          },
          { passive: true }
        );

        slider.addEventListener(
          'touchend',
          () => {
            if (this.mql.matches) return;

            this.isTouchingGallery = false;
            this.scheduleSingleSlideCorrection(viewer);
          },
          { passive: true }
        );

        slider.addEventListener(
          'scroll',
          () => {
            if (this.mql.matches || this.isCorrectingSwipe || this.isTouchingGallery) return;

            this.scheduleSingleSlideCorrection(viewer);
          },
          { passive: true }
        );
      }

      scheduleSingleSlideCorrection(viewer) {
        window.clearTimeout(this.swipeSettleTimer);

        this.swipeSettleTimer = window.setTimeout(() => {
          this.correctGallerySwipeToSingleSlide(viewer);
        }, 140);
      }

      getClosestGallerySlideIndex(viewer, scrollLeft) {
        const slider = viewer.slider;
        const slides = viewer.sliderItems || Array.from(slider.querySelectorAll('.product__media-item'));

        if (!slides.length) return 0;

        return slides.reduce((closestIndex, slide, index) => {
          return Math.abs(slide.offsetLeft - scrollLeft) < Math.abs(slides[closestIndex].offsetLeft - scrollLeft)
            ? index
            : closestIndex;
        }, 0);
      }

      correctGallerySwipeToSingleSlide(viewer) {
        const slider = viewer.slider;
        const slides = viewer.sliderItems || Array.from(slider.querySelectorAll('.product__media-item'));

        if (!slides.length) return;

        const settledIndex = this.getClosestGallerySlideIndex(viewer, slider.scrollLeft);

        let direction = 0;

        if (settledIndex > this.swipeStartIndex) direction = 1;
        if (settledIndex < this.swipeStartIndex) direction = -1;

        const targetIndex = Math.max(0, Math.min(this.swipeStartIndex + direction, slides.length - 1));

        if (targetIndex === settledIndex) return;

        this.isCorrectingSwipe = true;

        slider.scrollTo({
          left: slides[targetIndex].offsetLeft,
          behavior: 'smooth',
        });

        window.setTimeout(() => {
          this.isCorrectingSwipe = false;
          this.swipeStartIndex = targetIndex;
          this.swipeStartLeft = slides[targetIndex].offsetLeft;
        }, 400);
      }

      scheduleCollapsibleMediaSetup() {
        window.clearTimeout(this.initialCollapsibleMediaTimer);
        window.clearTimeout(this.secondaryCollapsibleMediaTimer);

        this.classList.add('is-gallery-updating');

        this.initialCollapsibleMediaTimer = window.setTimeout(() => {
          this.setupCollapsibleMedia();

          this.secondaryCollapsibleMediaTimer = window.setTimeout(() => {
            this.setupCollapsibleMedia();
            this.classList.remove('is-gallery-updating');
          }, 400);
        }, 700);
      }

      getCurrentViewer() {
        return (
          Array.from(this.querySelectorAll('[id^="GalleryViewer"]')).find((viewer) => {
            const styles = window.getComputedStyle(viewer);
            return styles.display !== 'none' && styles.opacity !== '0';
          }) || this.elements.viewer
        );
      }

      clearCollapsedMediaItems() {
        this.querySelectorAll('.product__media-item').forEach((item) => {
          item.classList.remove('is-gallery-collapsed');
        });
      }

      getVisibleUniqueMediaItems() {
        const viewer = this.getCurrentViewer();
        const mediaItems = Array.from(viewer.querySelectorAll('.product__media-item'));
        const visibleUniqueItems = [];
        const seenMediaIds = new Set();

        mediaItems.forEach((item) => {
          const mediaId = item.dataset.mediaId;
          if (!mediaId || seenMediaIds.has(mediaId)) return;

          const rect = item.getBoundingClientRect();
          const display = window.getComputedStyle(item).display;

          if (display === 'none' || rect.width <= 0 || rect.height <= 0) return;

          seenMediaIds.add(mediaId);
          visibleUniqueItems.push(item);
        });

        return visibleUniqueItems;
      }

      updateCollapsedMediaItems() {
        this.clearCollapsedMediaItems();

        if (!this.enableCollapsedGallery) return;
        if (this.isExpanded || !this.mql.matches) return;

        const visibleUniqueItems = this.getVisibleUniqueMediaItems();

        visibleUniqueItems.slice(3).forEach((item) => {
          item.classList.add('is-gallery-collapsed');
        });
      }

      setupCollapsibleMedia() {
        if (!this.toggleButton) return;

        if (!this.enableCollapsedGallery) {
          this.clearCollapsedMediaItems();
          this.classList.remove('has-collapsible-media', 'is-expanded');
          this.toggleButton.hidden = true;
          this.toggleButton.setAttribute('aria-hidden', 'true');
          return;
        }

        window.clearTimeout(this.collapsibleMediaTimer);

        this.collapsibleMediaTimer = window.setTimeout(() => {
          this.clearCollapsedMediaItems();

          const visibleUniqueItems = this.getVisibleUniqueMediaItems();

          if (visibleUniqueItems.length <= 3 || !this.mql.matches) {
            this.clearCollapsedMediaItems();
            this.classList.remove('has-collapsible-media', 'is-expanded');
            this.toggleButton.hidden = true;
            this.toggleButton.setAttribute('aria-hidden', 'true');
            this.applyCollapsibleMediaState();
            return;
          }

          this.classList.add('has-collapsible-media');
          this.toggleButton.hidden = false;
          this.toggleButton.setAttribute('aria-hidden', 'false');

          this.applyCollapsibleMediaState();
          this.updateCollapsedMediaItems();
        }, 250);
      }

      applyCollapsibleMediaState() {
        if (!this.toggleButton) return;

        this.classList.toggle('is-expanded', this.isExpanded);
        this.toggleButton.setAttribute('aria-expanded', String(this.isExpanded));

        if (this.toggleLabel) {
          this.toggleLabel.textContent = this.isExpanded ? 'Show less images' : 'Show more images';
        }
      }

      scrollGalleryIntoView() {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const viewer = this.getCurrentViewer();

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (viewer.slider) viewer.resetPages();
        }

        this.scheduleCollapsibleMediaSetup();
        this.preventStickyHeader();

        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      // Colour-group gallery filter. A multi-colour product holds every colourway's
      // media in one gallery; this shows only the group (ARTICLE-COLOR-SEASON) of the
      // selected variant's featured media and hides the rest, across the main slides,
      // the thumbnail strip, and the (sibling) modal. `mediaDomId` is the newly active
      // media's "{section}-{media.id}"; `sectionId` locates the modal element.
      // Returns the active colour-group (or null) so callers can derive the article number.
      filterByColorGroupForMedia(mediaDomId, sectionId) {
        const anchor =
          this.querySelector(`[data-media-id="${mediaDomId}"][data-color-group]`) ||
          this.querySelector('[data-color-group]');
        if (!anchor) return null;

        const activeGroup = anchor.dataset.colorGroup;
        // Unparseable anchor -> show everything (degrade to current behaviour).
        if (!activeGroup || activeGroup === '__ungrouped__') {
          this.clearColorGroupFilter(sectionId);
          return null;
        }
        this.applyColorGroupFilter(activeGroup, sectionId);
        return activeGroup;
      }

      applyColorGroupFilter(activeGroup, sectionId) {
        // Never hide an unparseable item (group === '__ungrouped__') or the active group.
        const shouldHide = (el) => {
          const g = el.dataset.colorGroup;
          return !!g && g !== '__ungrouped__' && g !== activeGroup;
        };

        this.querySelectorAll('.product__media-item[data-color-group]').forEach((el) => {
          el.classList.toggle('product__media-item--group-hidden', shouldHide(el));
        });
        this.querySelectorAll('.thumbnail-list__item[data-color-group]').forEach((el) => {
          el.classList.toggle('thumbnail-list__item--group-hidden', shouldHide(el));
        });

        const modal = sectionId ? document.getElementById(`ProductModal-${sectionId}`) : null;
        if (modal) {
          modal.querySelectorAll('.product-media-modal__content > [data-color-group]').forEach((el) => {
            el.classList.toggle('product__media-item--group-hidden', shouldHide(el));
          });
        }

        // Visibility changed: recompute slider pages + the "show first 3" collapse
        // against the now-visible set (both already filter by computed display).
        this.getCurrentViewer()?.resetPages?.();
        this.scheduleCollapsibleMediaSetup();
      }

      clearColorGroupFilter(sectionId) {
        this.querySelectorAll('.product__media-item--group-hidden').forEach((el) =>
          el.classList.remove('product__media-item--group-hidden')
        );
        this.querySelectorAll('.thumbnail-list__item--group-hidden').forEach((el) =>
          el.classList.remove('thumbnail-list__item--group-hidden')
        );
        const modal = sectionId ? document.getElementById(`ProductModal-${sectionId}`) : null;
        modal?.querySelectorAll('.product__media-item--group-hidden').forEach((el) =>
          el.classList.remove('product__media-item--group-hidden')
        );

        this.getCurrentViewer()?.resetPages?.();
        this.scheduleCollapsibleMediaSetup();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));

        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const viewer = this.getCurrentViewer();
        if (!viewer.slider) return;

        viewer.slider.setAttribute('role', 'presentation');
        viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}